import {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  useRef,
  useState,
} from "react";
import * as styles from "./BlockCardCarousel.css";
import { Heading } from "~/components/elements/Heading";
import PortableText, {
  IPortableText,
} from "~/components/portableText/PortableText";
import { ButtonLink } from "~/components/elements/ButtonLink";
import { Button } from "~/components/elements/Button";
// @ts-ignore
import ChevronLeft from "@material-design-icons/svg/filled/chevron_left.svg?react";
// @ts-ignore
import ChevronRight from "@material-design-icons/svg/filled/chevron_right.svg?react";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import { Swiper as SwiperType } from "swiper/types";
import Grid from "~/components/globals/Grid";
import { ILinkProps } from "~/components/elements/Link";
// @ts-ignore
import ArrowOutward from "@material-design-icons/svg/filled/arrow_outward.svg?react";
import { Paragraph } from "~/components/elements/Paragraph";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

export type BlockCardCarouselProps = ComponentPropsWithoutRef<"div"> &
  PropsWithChildren & {
    theme?: "colour" | "light";
    text?: string;
    title?: string;
    link?: ILinkProps;
    layout?: "small" | "default";
  };

export const BlockCardCarousel = ({
  theme = "colour",
  layout = "default",
  children,
  ...props
}: BlockCardCarouselProps) => {
  const { title, text, link } = props;
  const swiperRef = useRef<SwiperType>();
  const carouselRef = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  useGSAP(
    () => {
      const slides = carouselRef.current?.querySelectorAll(".swiper-slide");
      if (!slides?.length) return;
      const duration = 0.5;
      const ease = "power1.inOut";
      gsap.to(slides, { scale: 0.9, opacity: 0.5, duration, ease });
      gsap.to(slides[activeIndex], { scale: 1, opacity: 1, duration, ease });
    },
    { scope: carouselRef, dependencies: [activeIndex] },
  );

  return (
    <Grid className={styles.grid({ theme: theme })}>
      <div className={styles.header}>
        <div className={styles.content}>
          <Heading as="h2" size="h1" uppercase bold>
            {title}
          </Heading>
          {text && (
            <Paragraph size="md" className={styles.text({ theme: theme })}>
              {text}
            </Paragraph>
          )}
        </div>
        <div className={styles.buttons}>
          {link && link.slug && (
            <ButtonLink
              theme={theme === "light" ? "colour" : "light"}
              link={link}
              icons={{ iconPrimary: <ArrowOutward /> }}
            >
              {link.text}
            </ButtonLink>
          )}
          <Button
            icons={{ iconPrimary: <ChevronLeft /> }}
            theme={"light"}
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={
              !Array.isArray(children) ||
              children.length <= 0 ||
              activeIndex === 0 ||
              swiperRef.current?.isBeginning
            }
          />
          <Button
            icons={{ iconPrimary: <ChevronRight /> }}
            theme={"light"}
            onClick={() => swiperRef.current?.slideNext()}
            disabled={
              !Array.isArray(children) ||
              children.length <= 0 ||
              swiperRef.current?.isEnd ||
              activeIndex >= children.length - 1
            }
          />
        </div>
      </div>
      <div ref={carouselRef} className={styles.carousel}>
        <Swiper
          onInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          spaceBetween={10}
          speed={500}
          slidesPerView={1.15}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          loop={false}
          breakpoints={{
            768: {
              slidesPerView: layout === "small" ? 1.8 : 1.3,
              spaceBetween: 20,
            },
            2000: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
          }}
        >
          {Array.isArray(children) &&
            children.map((child, index) => {
              return (
                <div style={{ display: "contents" }} key={index}>
                  <SwiperSlide key={index} className={styles.swiperSlide}>
                    {child}
                  </SwiperSlide>
                </div>
              );
            })}
          <SwiperSlide className={styles.swiperSlide}>
            <div></div>
          </SwiperSlide>
        </Swiper>
      </div>

      <div className={styles.timeline({ theme: theme })}>
        <div
          className={styles.timelineBar({ theme: theme })}
          style={{
            width: Array.isArray(children)
              ? `${((activeIndex + 1) / children.length) * 100}%`
              : "20%",
          }}
        ></div>
      </div>
    </Grid>
  );
};
